'use client';

import { useRouter } from 'next/navigation';
import { useGoBack } from '@/hooks/use-go-back';
import { Separator, Skeleton, toast } from '@mokoboko/components';
import { PageContainer } from './page-container';
import {
  DetailBuyButton,
  DetailCard,
  DetailTopBar,
  DETAIL_HERO,
  DETAIL_HERO_MEDIA,
  DETAIL_HERO_TEXT,
} from './detail-card';
import { AppearanceSettings } from '@/state/appearance-settings';
import { mergeClassNames } from '@/lib/utils';
import { Subscription } from '@/state/subscription-groups';
import { trackMetaConversion } from '@/lib/analytics';
import { track, AnalyticsEvent } from '@/lib/analytics/tracker';
import { CartData } from '@/lib/types';
import { goToCheckout } from '@/helpers/checkout-navigation';
import { useAuth } from '@/contexts/auth';
import useCustomerHistory from '@/state/customer-history';
import usePurchaseEligibility from '@/state/purchase-eligibility';
import useTenant from '@/state/tenant';
import { ImageGallery, GALLERY_SKELETON } from './image-gallery';
import { getSortedImages } from '@/lib/entity-image';
import { DESCRIPTION_PROSE } from '@/lib/prose';
import { getSubscriptionFacts } from '@/helpers/offering-facts';
import { isSinglePayment } from '@/helpers/subscription';
import {
  OfferingFactTiles,
  OfferingHeroFacts,
  OfferingHeroFactsSkeleton,
} from './offering-facts';
import { OfferingDetailSections } from './offering-detail-sections';
import { BOUGHT_BEFORE } from './restriction-alert';

export const SubscriptionDetails = ({
  subscription,
  subscriptionSettings,
}: {
  subscription: Subscription;
  subscriptionSettings: AppearanceSettings['subscription'];
}) => {
  const router = useRouter();
  const { goBack, hasBack } = useGoBack();
  const { user } = useAuth();
  const shouldCheckNewCustomer = !!user && !!subscription.new_customers_only;
  const { isNewCustomer } = useCustomerHistory({
    userId: shouldCheckNewCustomer ? user!.user_id : undefined,
  });
  const showNewCustomerWarning = shouldCheckNewCustomer && !isNewCustomer;

  const { subscriptionEligibility } = usePurchaseEligibility({
    userId: user?.user_id,
    subscriptionId: user ? subscription.subscription_id : undefined,
  });
  const eligibilityReason = subscriptionEligibility?.reason ?? null;
  const showAlreadyPurchasedWarning = eligibilityReason === 'ALREADY_PURCHASED';
  const showActiveSubscriptionWarning =
    eligibilityReason === 'ACTIVE_SUBSCRIPTION_EXISTS';
  const showEligibilityWarning =
    showAlreadyPurchasedWarning || showActiveSubscriptionWarning;
  const eligibilityWarningText = showAlreadyPurchasedWarning
    ? BOUGHT_BEFORE.subscription.reason
    : 'You already have an active membership';

  const restrictionReason = showNewCustomerWarning
    ? 'This membership is available for new customers only'
    : showEligibilityWarning
      ? eligibilityWarningText
      : undefined;
  const restrictionDetail =
    !showNewCustomerWarning && showAlreadyPurchasedWarning
      ? BOUGHT_BEFORE.subscription.detail
      : null;

  const descriptionText =
    subscription.description ?? subscription.short_description;

  const sortedImages = subscription.images
    ? getSortedImages(subscription.images)
    : [];
  const showImage = subscriptionSettings.show_image;
  const { data: tenant } = useTenant();
  const facts = getSubscriptionFacts(subscription, subscriptionSettings, tenant?.name);
  const hasImages = sortedImages.length > 0 || !!subscription.image;

  const handleCheckout = () => {
    const cartData: CartData<'subscription'> = {
      items: [
        {
          ...subscription,
          quantity: 1,
          price: subscription.price ?? 0,
        },
      ],
      totalAmount: subscription.price ?? 0,
      checkoutType: 'subscription',
    };

    if (!goToCheckout(router, cartData)) {
      toast.info('Allow pop-ups to continue to checkout.');
      return;
    }

    trackMetaConversion({
      event: 'AddToCart',
      custom_data: {
        content_name: subscription.name,
        content_type: 'subscription',
        content_ids: [subscription.slug],
        value: subscription.price ?? 0,
        ...(subscription.currency && { currency: subscription.currency }),
      },
    });
    track({
      event: AnalyticsEvent.PRODUCT_ADDED_TO_CART,
      properties: {
        product_type: 'subscription',
        product_name: subscription.name,
        product_id: subscription.subscription_id,
        price: subscription.price,
        currency: subscription.currency ?? null,
        quantity: 1,
      },
    });
  };

  const buyButton = {
    label: isSinglePayment(subscription) ? 'Buy membership' : 'Subscribe',
    reason: restrictionReason,
    reasonDetail: restrictionDetail,
    onClick: handleCheckout,
    reasonAction:
      showActiveSubscriptionWarning && !showNewCustomerWarning
        ? {
            label: 'Manage membership',
            onClick: () => router.push('/dashboard/credits'),
          }
        : {
            label: 'Select another',
            onClick: () => router.push('/subscriptions'),
          },
  };

  return (
    <PageContainer>
      <DetailCard hasBack={hasBack}>
        <DetailTopBar
          hasBack={hasBack}
          onBack={() => goBack('/subscriptions')}
          buyButton={buyButton}
        />

        <div className={DETAIL_HERO}>
          <div
            className={mergeClassNames(
              DETAIL_HERO_TEXT,
              hasImages && showImage ? 'order-2 md:order-1' : ''
            )}
          >
            <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
              <div className="flex flex-1 flex-col gap-5 sm:gap-2">
                <h1 className="heading-page">{subscription.name}</h1>
                <OfferingHeroFacts facts={facts} />
              </div>
              {!hasBack && <DetailBuyButton {...buyButton} />}
            </div>

            <DetailBuyButton {...buyButton} phone />

            {subscriptionSettings.show_description && descriptionText && (
              <div
                className={DESCRIPTION_PROSE}
                dangerouslySetInnerHTML={{ __html: descriptionText }}
              />
            )}
          </div>

          {hasImages && showImage && (
            <div className={DETAIL_HERO_MEDIA}>
              <ImageGallery
                images={sortedImages}
                alt={subscription.name}
                fallbackUrl={subscription.image}
              />
            </div>
          )}
        </div>

        <OfferingFactTiles facts={facts} />

        <OfferingDetailSections
          settings={subscriptionSettings}
          classIds={
            subscription.subscription_classes?.map((c) => c.class_id) ?? []
          }
          productType="subscription"
          slug={subscription.slug}
        />
      </DetailCard>
    </PageContainer>
  );
};

export const SubscriptionDetailsSkeleton = () => {
  const { hasBack } = useGoBack();

  return (
    <PageContainer>
      <DetailCard hasBack={hasBack}>
        {hasBack && (
          <>
            <div className="flex items-center justify-between">
              <Skeleton
                isRounded
                className="!h-11 w-[124px] sm:!h-10 sm:w-[108px]"
              />
              <Skeleton
                isRounded
                className="hidden !h-10 sm:block sm:w-[98px]"
              />
            </div>
            <Separator />
          </>
        )}

        <div className={DETAIL_HERO}>
          <div
            className={mergeClassNames(DETAIL_HERO_TEXT, 'order-2 md:order-1')}
          >
            <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
              <div className="flex flex-1 flex-col gap-5 sm:gap-2">
                <Skeleton text="h2" width={216} />
                <OfferingHeroFactsSkeleton />
              </div>
              {!hasBack && (
                <Skeleton
                  isRounded
                  className="hidden !h-10 shrink-0 sm:block sm:w-[98px]"
                />
              )}
            </div>

            <div className="sm:hidden">
              <Skeleton isRounded height={44} width="100%" />
            </div>

            <div className="flex flex-col gap-2">
              <Skeleton text="body-sm" />
              <Skeleton text="body-sm" />
              <Skeleton text="body-sm" className="w-2/3" />
            </div>
          </div>

          <div className={DETAIL_HERO_MEDIA}>
            <Skeleton isRounded className={GALLERY_SKELETON} />
          </div>
        </div>
      </DetailCard>
    </PageContainer>
  );
};
